import React from "react";
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip } from "@progress/kendo-react-charts";
import { Card } from "../../components/analytics-kit";
import ChartBoundary from "../market-intelligence/chart-boundary";
import { channelLabel, num, usd } from "./common";

/*
 * By month (DS 2026-10-21): the last 12 calendar months, stacked by channel, on Campaigns →
 * Overview and on each market. Data = `months` on GET /v1/campaign/ad/overview and
 * /markets/{name} — one row per month regardless of the page's date range (a month view of
 * a 7-day range would be a single bar). The current month is to date. Spend only for
 * tenants that see money (the server also scrubs it for managed tenants).
 */
const CHANNEL_COLORS = {
  Display: "var(--plk-chart-display)",
  Retarget: "var(--plk-chart-retarget)",
  Search: "var(--plk-chart-search)",
  Social: "var(--plk-chart-social)",
  Video: "var(--plk-chart-video)",
  YouTube: "var(--plk-chart-youtube)",
  "CTV/OTT": "var(--plk-chart-ctv)",
  Pinterest: "var(--plk-chart-pinterest)"
};
const METRICS = [["impressions", "Impressions"], ["clicks", "Clicks"], ["videoViews", "Video views"], ["spend", "Spend"]];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export default class MonthsChart extends React.Component {
  constructor(p) {
    super(p);
    this.state = {
      metric: "impressions"
    };
  }
  render() {
    const months = this.props.months || [];
    if (!months.length) return null;
    const money = !!this.props.money;
    const metric = !money && this.state.metric === "spend" ? "impressions" : this.state.metric;
    const channels = Array.from(new Set(months.flatMap(m => (m.byChannel || []).map(c => c.type)))).filter(Boolean);
    const valueOf = (m, c) => {
      const r = (m.byChannel || []).find(x => x.type === c);
      return r ? Number(r[metric]) || 0 : 0;
    };
    const total = months.reduce((s, m) => s + (Number(m[metric]) || 0), 0);
    const fmt = metric === "spend" ? usd : num;
    const labels = months.map((m, i) => {
      const [y, mo] = String(m.month).split("-").map(Number);
      return `${MONTHS[mo - 1]}${i === 0 || mo === 1 ? ` ’${String(y).slice(2)}` : ""}${m.partial ? "*" : ""}`;
    });
    const hint = `last 12 months · ${fmt(total)} total · stacked by channel${months.some(m => m.partial) ? " · * month to date" : ""}`;
    return (
      /*#__PURE__*/
      // same grid wrapper as the cards around it, so it lines up and spaces like them (DS 2026-10-22)
      React.createElement("div", {
        className: "campaign-grid"
      }, /*#__PURE__*/React.createElement("div", {
        className: "campaign-grid-cell"
      }, /*#__PURE__*/React.createElement(Card, {
        title: this.props.title || "By month",
        hint: hint,
        right: /*#__PURE__*/React.createElement("span", {
          className: "seg"
        }, METRICS.filter(([m]) => money || m !== "spend").map(([m, l]) => /*#__PURE__*/React.createElement("button", {
          key: m,
          className: metric === m ? "on" : "",
          onClick: () => this.setState({
            metric: m
          })
        }, l)))
      }, total === 0 ? /*#__PURE__*/React.createElement("p", {
        className: "territory-hint"
      }, "No ", (METRICS.find(([m]) => m === metric) || [, ""])[1].toLowerCase(), " recorded in the last 12 months.") : /*#__PURE__*/React.createElement(ChartBoundary, null, /*#__PURE__*/React.createElement(Chart, {
        style: {
          height: this.props.height || 240
        },
        transitions: false
      }, /*#__PURE__*/React.createElement(ChartLegend, {
        position: "bottom"
      }), /*#__PURE__*/React.createElement(ChartTooltip, {
        visible: true,
        format: metric === "spend" ? "{0:c2}" : "{0:n0}"
      }), /*#__PURE__*/React.createElement(ChartCategoryAxis, null, /*#__PURE__*/React.createElement(ChartCategoryAxisItem, {
        categories: labels,
        majorGridLines: {
          visible: false
        }
      })), /*#__PURE__*/React.createElement(ChartValueAxis, null, /*#__PURE__*/React.createElement(ChartValueAxisItem, {
        labels: {
          format: metric === "spend" ? "{0:c0}" : "{0:n0}"
        }
      })), /*#__PURE__*/React.createElement(ChartSeries, null, channels.map(c => /*#__PURE__*/React.createElement(ChartSeriesItem, {
        key: c,
        type: "column",
        stack: true,
        name: channelLabel(c),
        data: months.map(m => valueOf(m, c)),
        color: CHANNEL_COLORS[c] || "var(--plk-chart-stable)",
        gap: 0.6
      }))))))))
    );
  }
}